import { html } from '@design.estate/dees-element';
import type { DeesHarnessChat } from './dees-harness-chat.js';
import type { IHarnessMessage, IHarnessPermissionRequest } from '../interfaces.js';
import { harnessDemoConversation } from '../harness.demodata.js';

const toolShowcase = (): IHarnessMessage[] => {
  const now = Date.now();
  return [
    {
      id: 'demo-t1',
      role: 'tool',
      text: '',
      createdAt: now - 240_000,
      toolCall: {
        id: 'call-1',
        name: 'run_command',
        status: 'completed',
        input: { command: 'pnpm test --filter sitemap', cwd: '/srv/app' },
        output: { exitCode: 0, stdout: '14 tests passed' },
        startedAt: now - 250_000,
        finishedAt: now - 243_000,
      },
    },
    {
      id: 'demo-t2',
      role: 'tool',
      text: '',
      createdAt: now - 200_000,
      toolCall: {
        id: 'call-2',
        name: 'write_file',
        status: 'completed',
        input: {
          path: '/srv/app/src/jobs/sitemap.job.ts',
          oldContent: "const cacheKey = hash(locales);",
          content: "const cacheKey = hash(routeManifest);",
        },
        startedAt: now - 205_000,
        finishedAt: now - 202_000,
      },
    },
    {
      id: 'demo-t3',
      role: 'tool',
      text: '',
      createdAt: now - 150_000,
      toolCall: {
        id: 'call-3',
        name: 'kb__lookup_article',
        status: 'completed',
        mcp: {
          serverName: 'kb',
          toolName: 'lookup_article',
          annotations: { title: 'Knowledge Lookup', readOnlyHint: true, openWorldHint: true },
        },
        input: { query: 'sitemap cache determinism' },
        contentBlocks: [
          { type: 'text', text: 'Found “Deterministic sitemap builds”.' },
          {
            type: 'resource_link',
            uri: 'https://kb.example.com/articles/812',
            name: 'deterministic-sitemap-builds',
            title: 'Deterministic sitemap builds',
            mimeType: 'text/html',
          },
        ],
        startedAt: now - 155_000,
        finishedAt: now - 152_000,
      },
    },
  ];
};

const pendingPermission: IHarnessPermissionRequest = {
  id: 'perm-demo',
  title: 'Write file',
  type: 'write',
  createdAt: Date.now(),
  metadata: { path: '/srv/app/src/jobs/sitemap.job.ts', bytes: 812 },
};

let stopStream: (() => void) | undefined;

const chatOf = (event: MouseEvent): DeesHarnessChat | null =>
  (event.target as HTMLElement).closest('.chatDemo')?.querySelector('dees-harness-chat') ?? null;

/**
 * Streams a realistic turn: reasoning, a todo write, a test run, a file
 * edit rendered as a diff, then the streamed summary text.
 */
const simulate = (event: MouseEvent) => {
  const chat = chatOf(event);
  if (!chat) return;
  stopStream?.();
  const runId = Date.now();
  const cancels: Array<() => void> = [];
  const at = (ms: number, action: () => void) => {
    const timeout = setTimeout(action, ms);
    cancels.push(() => clearTimeout(timeout));
  };
  stopStream = () => {
    for (const cancel of cancels) cancel();
    chat.status = { type: 'idle' };
  };
  const pushMessage = (message: IHarnessMessage) => {
    chat.messages = [...chat.messages, message];
  };
  const updateTool = (messageId: string, tool: Partial<IHarnessMessage['toolCall']> & { id: string }) => {
    chat.applyDelta({ type: 'tool-update', messageId, tool: tool as never });
  };

  chat.status = { type: 'busy', message: 'Planning…' };

  // 1) reasoning streams first
  const reasoningMessage: IHarnessMessage = {
    id: `sim-${runId}-reasoning`, role: 'assistant', text: '', createdAt: Date.now(), streaming: true,
  };
  pushMessage(reasoningMessage);
  chat.applyDelta({ type: 'reasoning-start', messageId: reasoningMessage.id, partId: 'r1', startedAt: Date.now() });
  const reasoningText = 'The cache key includes the locale list, so every rollout re-crawls. Plan: track the fix as todos, run the tests, then pin the key to the route manifest.';
  reasoningText.split(' ').forEach((word, index) => {
    at(80 + index * 45, () => chat.applyDelta({ type: 'reasoning', messageId: reasoningMessage.id, partId: 'r1', delta: word + ' ' }));
  });
  const reasoningDoneAt = 80 + reasoningText.split(' ').length * 45 + 150;
  at(reasoningDoneAt, () => {
    chat.applyDelta({ type: 'reasoning-end', messageId: reasoningMessage.id, partId: 'r1', endedAt: Date.now() });
    chat.applyDelta({ type: 'message-end', messageId: reasoningMessage.id });
  });

  // 2) todo write
  const todoId = `sim-${runId}-todos`;
  at(reasoningDoneAt + 300, () => {
    chat.status = { type: 'busy', message: 'Updating task list…' };
    pushMessage({
      id: todoId, role: 'tool', text: '', createdAt: Date.now(),
      toolCall: {
        id: `${todoId}-call`, name: 'todowrite', status: 'running',
        input: { todos: [
          { content: 'Pin sitemap cache key to route manifest hash', status: 'in_progress' },
          { content: 'Run the sitemap test suite', status: 'pending' },
          { content: 'Apply the cache-key patch', status: 'pending' },
          { content: 'Verify staging timings', status: 'pending' },
        ] },
        startedAt: Date.now(),
      },
    });
  });
  at(reasoningDoneAt + 1000, () => updateTool(todoId, { id: `${todoId}-call`, status: 'completed', finishedAt: Date.now() }));

  // 3) test run
  const testId = `sim-${runId}-test`;
  at(reasoningDoneAt + 1400, () => {
    chat.status = { type: 'busy', message: 'Running tests…' };
    pushMessage({
      id: testId, role: 'tool', text: '', createdAt: Date.now(),
      toolCall: {
        id: `${testId}-call`, name: 'run_command', status: 'running',
        input: { command: 'pnpm test --filter sitemap', cwd: '/srv/app' },
        startedAt: Date.now(),
      },
    });
  });
  at(reasoningDoneAt + 3200, () => updateTool(testId, {
    id: `${testId}-call`, status: 'completed',
    output: { exitCode: 0, stdout: '14 tests passed\n0 failed' },
    finishedAt: Date.now(),
  }));

  // 4) file edit rendered as a diff
  const editId = `sim-${runId}-edit`;
  at(reasoningDoneAt + 3600, () => {
    chat.status = { type: 'busy', message: 'Applying the patch…' };
    pushMessage({
      id: editId, role: 'tool', text: '', createdAt: Date.now(),
      toolCall: {
        id: `${editId}-call`, name: 'write_file', status: 'running',
        input: {
          path: '/srv/app/src/jobs/sitemap.job.ts',
          oldContent: 'const cacheKey = hash(locales);\nconst ttl = 3600;',
          content: 'const cacheKey = hash(routeManifest);\nconst ttl = 3600;',
        },
        startedAt: Date.now(),
      },
    });
  });
  at(reasoningDoneAt + 4600, () => updateTool(editId, { id: `${editId}-call`, status: 'completed', finishedAt: Date.now() }));

  // 5) updated todos, then the streamed summary
  const todoDoneId = `sim-${runId}-todos-2`;
  at(reasoningDoneAt + 5000, () => {
    pushMessage({
      id: todoDoneId, role: 'tool', text: '', createdAt: Date.now(),
      toolCall: {
        id: `${todoDoneId}-call`, name: 'todowrite', status: 'completed',
        input: { todos: [
          { content: 'Pin sitemap cache key to route manifest hash', status: 'completed' },
          { content: 'Run the sitemap test suite', status: 'completed' },
          { content: 'Apply the cache-key patch', status: 'completed' },
          { content: 'Verify staging timings', status: 'in_progress' },
        ] },
        startedAt: Date.now(), finishedAt: Date.now(),
      },
    });
  });
  const summaryId = `sim-${runId}-summary`;
  at(reasoningDoneAt + 5400, () => {
    chat.status = { type: 'busy', message: 'Responding…' };
    pushMessage({ id: summaryId, role: 'assistant', text: '', createdAt: Date.now(), streaming: true });
    const summary = 'Patched `sitemap.job.ts` to key the cache on the **route manifest hash** — tests pass (14/14). Staging verification is the last open task.';
    let offset = 0;
    const interval = setInterval(() => {
      const chunk = summary.slice(offset, offset + 6);
      offset += 6;
      if (!chunk) {
        clearInterval(interval);
        chat.applyDelta({ type: 'message-end', messageId: summaryId, usage: { inputTokens: 2100, outputTokens: 260, totalTokens: 2360 } });
        chat.status = { type: 'idle' };
        return;
      }
      chat.applyDelta({ type: 'text', messageId: summaryId, delta: chunk });
    }, 40);
    cancels.push(() => clearInterval(interval));
  });
};

const askQuestion = (event: MouseEvent) => {
  const chat = chatOf(event);
  if (!chat) return;
  chat.questions = [
    ...chat.questions,
    {
      id: `question-${Date.now()}`,
      question: 'Which cache-key strategy should I roll out?',
      createdAt: Date.now(),
      options: [
        { label: 'Route manifest hash', description: 'Deterministic, invalidates on route changes' },
        { label: 'Content hash', description: 'Fine-grained, more churn' },
      ],
    },
  ];
};

const askPermission = (event: MouseEvent) => {
  const chat = chatOf(event);
  if (!chat) return;
  chat.permissions = [...chat.permissions, { ...pendingPermission, id: `perm-${Date.now()}` }];
};

const toggleError = (event: MouseEvent) => {
  const chat = chatOf(event);
  if (!chat) return;
  chat.status = chat.status.type === 'error'
    ? { type: 'idle' }
    : { type: 'error', message: 'The model endpoint returned 429 — retrying in 20s.' };
};

const resetChat = (event: MouseEvent) => {
  const chat = chatOf(event);
  if (!chat) return;
  stopStream?.();
  chat.messages = [...harnessDemoConversation(), ...toolShowcase()];
  chat.permissions = [];
  chat.status = { type: 'idle' };
};

export const demoFunc = () => html`
  <style>
    .chatDemo {
      display: grid;
      grid-template-rows: auto minmax(0, 1fr);
      gap: 12px;
      height: 640px;
      max-width: 860px;
      margin: 40px auto;
    }
    .chatDemo .controls {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }
    .chatDemo dees-harness-chat {
      border: 1px solid var(--dees-color-border-subtle);
      border-radius: var(--dees-radius-xl);
      overflow: hidden;
    }
  </style>
  <div
    class="chatDemo"
    @harness-send=${(event: CustomEvent) => console.log('harness-send', event.detail)}
    @harness-permission-response=${(event: CustomEvent) => {
      console.log('harness-permission-response', event.detail);
      const chat = (event.target as HTMLElement).closest('.chatDemo')?.querySelector('dees-harness-chat');
      if (chat) {
        // answered cards stay in the transcript showing the outcome
        chat.permissions = chat.permissions.map((request) =>
          request.id === event.detail.requestId
            ? { ...request, response: event.detail.response, respondedAt: Date.now() }
            : request,
        );
      }
    }}
    @harness-question-response=${(event: CustomEvent) => {
      console.log('harness-question-response', event.detail);
      const chat = (event.target as HTMLElement).closest('.chatDemo')?.querySelector('dees-harness-chat');
      if (chat) {
        chat.questions = chat.questions.map((request) =>
          request.id === event.detail.requestId
            ? { ...request, response: event.detail.answers, respondedAt: Date.now() }
            : request,
        );
      }
    }}
    @harness-new-session=${() => console.log('harness-new-session')}
    @harness-open-sessions=${() => console.log('harness-open-sessions')}
    @harness-abort=${() => {
      stopStream?.();
      console.log('harness-abort');
    }}
  >
    <div class="controls">
      <dees-button type="accent" @clicked=${simulate}>Simulate streaming</dees-button>
      <dees-button @clicked=${askPermission}>Ask permission</dees-button>
      <dees-button @clicked=${askQuestion}>Ask a question</dees-button>
      <dees-button @clicked=${toggleError}>Toggle error status</dees-button>
      <dees-button type="ghost" @clicked=${resetChat}>Reset</dees-button>
    </div>
    <dees-harness-chat
      heading="Deploy assistant"
      subheading="acme-app · main · connected"
      model="gpt-5.5"
      reasoningEffort="high"
      mode="Building"
      .modeOptions=${['Planning', 'Building']}
      .modelOptions=${['gpt-5.5', 'gpt-5', 'o4-mini']}
      .effortOptions=${['xhigh', 'high', 'medium', 'low']}
      .usage=${{ inputTokens: 18_400, outputTokens: 3_100, totalTokens: 21_500 }}
      .messages=${[...harnessDemoConversation(), ...toolShowcase()]}
    ></dees-harness-chat>
  </div>
`;
